/**
 * The card-level record policy, web-side: whether a card's items are working
 * notes or RECORDS — entries that freeze once made and are corrected rather
 * than edited (`cardDefinitions.options.record`).
 *
 * Mirrors `utils/cardRecordPolicy.js` in the API as far as READING goes. The
 * API is the authority: it refuses a late edit with 409 `item_locked`
 * whatever this file thinks. What this side needs the rule for is showing
 * the truth BEFORE the save — a form that looks editable and then refuses is
 * worse than one that says plainly that this record is closed.
 */

import { cardChain } from './cards';

export const DEFAULT_RECORD_POLICY = Object.freeze({ lock: { after: 'never' }, delete: 'allowed' });

const recordSetting = card => {
	const value = card?.options?.record;
	if (value === undefined || value === null) return undefined;
	return value;
};

/** The policy in force for a chain (parent-first, card last). */
export function recordPolicyForChain(chain = []) {
	for (let i = (chain || []).length - 1; i >= 0; i -= 1) {
		const setting = recordSetting(chain[i]);
		if (setting === undefined) continue;
		if (setting === false) return DEFAULT_RECORD_POLICY;
		const lock = { ...DEFAULT_RECORD_POLICY.lock, ...(setting.lock || {}) };
		const locks = lock.after && lock.after !== 'never';
		return {
			lock,
			delete: setting.delete || (locks ? 'manage-with-reason' : 'allowed'),
		};
	}
	return DEFAULT_RECORD_POLICY;
}

/** The policy for one card id, walking its parents. */
export function recordPolicyFor(cardsById = {}, rawId) {
	return recordPolicyForChain(cardChain(cardsById, rawId));
}

/** Does this card freeze its entries at all? */
export const cardLocksRecords = policy => !!(policy?.lock?.after && policy.lock.after !== 'never');

/**
 * Is this item closed to ordinary edits right now, and what should the UI
 * say about it? Mirrors the API's own rule, including the time-boxed unlock
 * a manager may have granted.
 */
export function lockStateFor(item, policy, now = new Date()) {
	if (!cardLocksRecords(policy)) return { locked: false };
	const until = item?.unlockedUntil ? new Date(item.unlockedUntil) : null;
	if (until && !Number.isNaN(until.getTime()) && until.getTime() > now.getTime()) {
		return { locked: false, unlocked: true, until };
	}
	if (policy.lock.after === 'create') {
		return { locked: true, reason: 'This record locked when it was made.' };
	}
	const minutes = Number(policy.lock.minutes) || 0;
	const created = item?.createdAt ? new Date(item.createdAt) : null;
	if (!created || Number.isNaN(created.getTime())) return { locked: false };
	const elapsed = (now.getTime() - created.getTime()) / 60000;
	if (elapsed < minutes) {
		return { locked: false, within: true, minutesLeft: Math.max(1, Math.ceil(minutes - elapsed)) };
	}
	return { locked: true, reason: `This record locked ${formatLockPeriod(minutes)} after it was made.` };
}

/* ---- the grace period's units -------------------------------------------
 * Stored as minutes (the API's one canonical field, `lock.minutes`); the
 * editor asks for a quantity and a unit and multiplies, and reads a stored
 * value back in the COARSEST unit that divides it evenly — 2880 re-opens as
 * "2 days", the number the author typed, not 2880 minutes. Mirrors
 * keepr-api utils/cardRecordPolicy.js (formatLockPeriod) so the lock
 * sentence reads the same on both sides.
 */

export const LOCK_MINUTES_MIN = 1;
/** The API's ceiling: a month. Past that, "locked" means locked. */
export const LOCK_MINUTES_MAX = 60 * 24 * 30;

export const LOCK_UNIT_MINUTES = { minute: 1, hour: 60, day: 1440 };

export const LOCK_UNIT_OPTIONS = [
	{ value: 'minute', label: 'Minute(s)' },
	{ value: 'hour', label: 'Hour(s)' },
	{ value: 'day', label: 'Day(s)' },
];

/** The largest quantity of a unit that still fits under the ceiling. */
export const lockQtyMax = unit => Math.floor(LOCK_MINUTES_MAX / (LOCK_UNIT_MINUTES[unit] || 1));

/** quantity + unit -> canonical minutes, clamped to what the API accepts. */
export function lockMinutesFrom(qty, unit) {
	const per = LOCK_UNIT_MINUTES[unit] || 1;
	const raw = Math.round((Number(qty) || 0) * per);
	if (!Number.isFinite(raw) || raw < LOCK_MINUTES_MIN) return LOCK_MINUTES_MIN;
	return Math.min(raw, LOCK_MINUTES_MAX);
}

/** Canonical minutes -> `{ qty, unit }`, the coarsest unit that divides evenly. */
export function splitLockMinutes(minutes) {
	const total = Number(minutes);
	if (!Number.isFinite(total) || total < LOCK_MINUTES_MIN) return { qty: 15, unit: 'minute' };
	const clamped = Math.min(Math.round(total), LOCK_MINUTES_MAX);
	if (clamped % LOCK_UNIT_MINUTES.day === 0) return { qty: clamped / LOCK_UNIT_MINUTES.day, unit: 'day' };
	if (clamped % LOCK_UNIT_MINUTES.hour === 0) return { qty: clamped / LOCK_UNIT_MINUTES.hour, unit: 'hour' };
	return { qty: clamped, unit: 'minute' };
}

/** "15 minutes", "2 hours", "1 day" — the grace period in its own words. */
export function formatLockPeriod(minutes) {
	const { qty, unit } = splitLockMinutes(minutes);
	return `${qty} ${unit}${qty === 1 ? '' : 's'}`;
}
